import { faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { useState } from 'react';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  laptopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import type { Colors } from '../getTaskColors';

const Container = styled.div`
  container-type: inline-size;
  width: 100%;
`;

const Inline = styled(Vertical).attrs({ gap: 2 })`
  --border-color: var(--c-nd-150-opaque);
  width: 100%;
  padding: var(--intermediate-unit);
  text-align: left;
  border-radius: var(--double-unit);
  border: 1px solid var(--border-color);
  background: linear-gradient(
      179deg,
      var(--c-black) -100%,
      rgba(var(--c-rgb-black), 0.9) 30%,
      var(--c-black)
    ),
    var(--c-black);

  &.red {
    border: 1px solid transparent;
    background:
      radial-gradient(
          150px 100% at 0% 0%,
          rgba(255, 78, 34, 0.6),
          rgba(0, 0, 0, 0) 100%
        )
        padding-box,
      linear-gradient(to bottom, #4f1000, rgba(var(--c-rgb-black), 0) 64%)
        padding-box,
      linear-gradient(to top, var(--c-black) 50%, transparent) padding-box,
      linear-gradient(var(--c-black), var(--c-black)) padding-box,
      linear-gradient(160deg, #ffa48d, var(--c-rewards-orange) 50%) border-box,
      var(--c-black);
    box-shadow: 0px 0px 8px 2px rgba(255, 78, 34, 0.2) inset;
  }

  &.yellow {
    border: 1px solid transparent;
    background:
      radial-gradient(
          150px 100% at 0% 0%,
          rgba(var(--c-rgb-yellow-600), 0.4),
          rgba(0, 0, 0, 0) 100%
        )
        padding-box,
      linear-gradient(to bottom, #211f00, rgba(var(--c-rgb-black), 0) 64%)
        padding-box,
      linear-gradient(to top, var(--c-black) 50%, transparent) padding-box,
      linear-gradient(var(--c-black), var(--c-black)) padding-box,
      linear-gradient(160deg, #f9c33e, var(--c-yellow-600) 50%) border-box,
      var(--c-black);
    box-shadow: 0px 0px 8px 2px rgba(247, 177, 0, 0.2) inset;
  }

  &.blue {
    border: 1px solid transparent;
    background:
      radial-gradient(
          150px 100% at 0% 0%,
          rgba(var(--c-rgb-superRare), 0.4),
          rgba(0, 0, 0, 0) 100%
        )
        padding-box,
      linear-gradient(
          to bottom,
          rgba(0, 0, 0, 0.5) 30.87%,
          rgba(0, 0, 0, 0.29) 68.73%,
          rgba(var(--c-rgb-black), 0) 64%
        )
        padding-box,
      linear-gradient(to top, var(--c-black) 50%, transparent) padding-box,
      linear-gradient(var(--c-black), var(--c-black)) padding-box,
      linear-gradient(
          160deg,
          rgba(var(--c-rgb-superRare), 1),
          rgba(var(--c-rgb-superRare), 1) 50%
        )
        border-box,
      var(--c-black);
    box-shadow: 0px 0px 8px 2px rgba(23, 158, 255, 0.2) inset;
  }

  &.vertical {
    flex-direction: column;
    height: 100%;
    padding: var(--intermediate-unit);
  }

  @container ${laptopAndAbove} {
    flex-direction: row;
    gap: var(--triple-unit);
  }
`;
const Content = styled(Vertical)`
  align-items: flex-start;

  & * {
    word-break: break-word;
  }

  @container ${laptopAndAbove} {
    flex: 1;
  }
`;
const ArrowWrapper = styled.div`
  padding: 0 var(--unit);
  align-self: center;
  display: none;

  @container ${laptopAndAbove} {
    display: block;
  }
`;
const FooterWrapper = styled.div`
  flex: 1;
`;
const FullWidthHorizontal = styled(Horizontal)`
  width: 100%;
  @media ${tabletAndAbove} {
    width: auto;
  }
`;

type Props = {
  className?: string;
  icon?: ReactNode;
  title?: ReactNode;
  description: ReactNode;
  footer: ReactNode;
  detailsDialog?: ({
    open,
    onClose,
  }: {
    open?: boolean;
    onClose: () => void;
  }) => ReactNode;
  color?: Colors;
  borderColor?: string | null;
  featured?: boolean;
  forceVertical?: boolean;
};
export const DumbTask = ({
  className,
  icon,
  title,
  description,
  footer,
  detailsDialog,
  color,
  borderColor,
  featured,
  forceVertical,
}: Props) => {
  const [dialogOpened, setDialogOpened] = useState(false);

  return (
    <Container>
      <Inline
        as={detailsDialog ? 'button' : 'div'}
        type={detailsDialog ? 'button' : undefined}
        onClick={() => !!detailsDialog && setDialogOpened(true)}
        className={classNames(className, color?.toLocaleLowerCase(), {
          vertical: featured || forceVertical,
        })}
        style={{ '--border-color': borderColor } as CSSProperties}
      >
        <Content>
          <FullWidthHorizontal>
            {icon}
            {title}
          </FullWidthHorizontal>
          {description}
        </Content>
        <FooterWrapper
          onClick={e => {
            e.stopPropagation();
          }}
        >
          {footer}
        </FooterWrapper>
        {!!detailsDialog && !featured && (
          <ArrowWrapper>
            <FontAwesomeIcon icon={faChevronRight} />
          </ArrowWrapper>
        )}
      </Inline>
      {detailsDialog?.({
        open: dialogOpened,
        onClose: () => setDialogOpened(false),
      })}
    </Container>
  );
};
